import { ActionButtonProperties, useActionButtonProperties } from "./ActionButtonProperties"
import { Button, ErrorMessage, Spinner } from "../../../../../widgets"
import { ControlType, FetchStatus } from "../../../../../base/types"
import { FC, useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { DraftActionButtonComponent } from "./DraftActionButtonComponent"
import { IControlAttributes } from "../../../../../base/models"
import { PageLinkHelper } from "../../../../../base/helpers"
import { Registry } from "../../../../../base/register"
import { useExecuteFormAction } from "./useExecuteFormAction"
import { useFormDataContext } from "../../form"

export const ActionButtonComponent: FC<IControlAttributes> = ({
    className, children, props
}) => {
    const { status, error, properties } = useActionButtonProperties(props);
    const { executeFunction } = useExecuteFormAction()

    const navigate = useNavigate()
    const { siteId } = useParams();
    const [isExecuting, setExecuting] = useState<boolean>(false)
    const { values, initValues, isSubmitting, setStatus } = useFormDataContext()
    const processing = useRef<boolean>(false)

    useEffect(() => {
        if (properties?.autoExecute !== true || status !== FetchStatus.Success || processing.current)
            return;

        const areEqualOrEmpty = () => {
            if (Object.keys(initValues).length === 0 || Object.keys(values).length === 0)
                return true;

            if (Object.keys(initValues).some(key => initValues[key] !== values[key]))
                return false;

            return true;
        }

        if (areEqualOrEmpty()) {
            return;
        }

        processing.current = true
        executeAction()
            .finally(() => processing.current = false)

        return () => { processing.current = false }
        // eslint-disable-next-line
    }, [values])

    const executeAction = () => {
        if (!properties)
            return Promise.reject();

        const { functionName, pageLink } = properties

        if (!functionName)
            return Promise.reject();

        setStatus('')
        setExecuting(true)
        return executeFunction(properties)
            .then(() => {
                if (siteId && pageLink)
                    navigate(PageLinkHelper.getPageLinkUrl(siteId, pageLink))
            })
            .catch((error) => {
                const message = error.data.message || 'An error occurred while processing the request';
                setStatus(message)
                return Promise.reject(error)
            })
            .finally(() => setExecuting(false))
            .finally(() => processing.current = false)
    }

    switch (status) {
        case FetchStatus.Idle: return <></>
        case FetchStatus.Loading: return <Spinner show={true} />
        case FetchStatus.Error: return <ErrorMessage message={error} />
    }

    const { functionName, systemId, pageLink, executeSave, autoExecute, disabled, ...buttonProps } = properties || {}

    return <Button
        {...buttonProps}
        onClick={executeAction}
        className={className}
        iconClassName="me-1"
        isLoading={isSubmitting || isExecuting}
        disabled={disabled || isSubmitting || isExecuting}
    >
        {children}
    </Button>
}

Registry.addControl({
    id: "cef5a854-5746-4a1c-a8be-d4e412858285",
    name: "Action Button",
    type: ControlType.ActionButton,
    properties: ActionButtonProperties,
    component: ActionButtonComponent,
    draftComponent: DraftActionButtonComponent
})
